import React, { useState, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Security,
  Description,
  Timer,
  Shield,
  VerifiedUser,
  GppBad,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import api from '../shared/services/api';
import { useTranslation } from '../store/languageStore';
import { brand } from '../theme';
import { stepperIcons } from '../assets/icons/nav';
import NavIcon from '../shared/components/NavIcon';

interface TamperingFlag {
  type: string;
  message: string;
}

interface TamperingResult {
  id: string;
  filename: string;
  file_hash: string;
  is_tampered: boolean;
  severity: string;
  risk_score: number;
  flags: TamperingFlag[];
  pdf_metadata: any;
  processing_time_ms: number;
}

const TamperingDetection: React.FC = () => {
  const theme = useTheme();
  const { tString } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<TamperingResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix (e.g., "data:application/pdf;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError(tString('tamperingDetection.pdfOnly'));
      return;
    }

    setUploading(true);
    setError(null);
    setResult(null);

    try {
      const base64Content = await fileToBase64(file);

      const uploadData = {
        filename: file.name,
        content: base64Content,
        content_type: file.type
      };

      const response = await api.post(
        '/api/v1/tampering-detection/analyze',
        uploadData,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      setResult(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || tString('tamperingDetection.analyzeFailed'));
    } finally {
      setUploading(false);
    }
  }, [tString]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
  });

  const getSeverityColor = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'success';
    }
  };

  const getRiskColor = (score: number) => {
    if (score === 0) return '#4caf50';
    if (score <= 3) return '#8bc34a';
    if (score <= 6) return '#ff9800';
    return '#f44336';
  };

  return (
    <Box sx={{ height: '100%', bgcolor: 'grey.50', overflow: 'auto', py: 4 }}>
        <Container maxWidth="lg">
          {/* Header Section */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <Shield sx={{ fontSize: 40, color: brand.orange }} />
            <Box>
              <Typography variant="h4" fontWeight={600} sx={{ color: brand.textPrimary }}>
                {tString('tamperingDetection.pageTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tString('tamperingDetection.pageSubtitle')}
              </Typography>
            </Box>
          </Stack>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            {/* Upload Section */}
            <Grid item xs={12} md={result ? 6 : 12}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  height: '100%',
                }}
              >
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      {tString('tamperingDetection.uploadTitle')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tString('tamperingDetection.uploadSubtitle')}
                    </Typography>
                  </Box>

                  <Paper
                    {...getRootProps()}
                    elevation={0}
                    sx={{
                      p: 6,
                      textAlign: 'center',
                      cursor: 'pointer',
                      border: '2px dashed',
                      borderColor: isDragActive ? brand.orange : brand.borderSubtle,
                      backgroundColor: brand.dropZoneBg,
                      transition: 'all 0.3s',
                      borderRadius: 1,
                      '&:hover': { borderColor: brand.orange },
                    }}
                  >
                    <input {...getInputProps()} />
                    <NavIcon src={stepperIcons.dragDrop} size={48} sx={{ mx: 'auto', mb: 1 }} />
                    <Typography variant="body1" gutterBottom sx={{ color: brand.textPrimary, fontWeight: 500 }}>
                      {isDragActive ? tString('tamperingDetection.dropHere') : tString('tamperingDetection.dragDrop')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: brand.textSecondary, display: 'block' }}>
                      {tString('tamperingDetection.browseHint')}
                    </Typography>
                  </Paper>

                  {uploading && (
                    <Box>
                      <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />
                      <Typography variant="body2" color="text.secondary" textAlign="center">
                        {tString('tamperingDetection.analyzing')}
                      </Typography>
                    </Box>
                  )}

                  {error && (
                    <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
                      {error}
                    </Alert>
                  )}
                </Stack>
              </Paper>
            </Grid>

            {/* Results Section */}
            {result && (
              <Grid item xs={12} md={6}>
                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'visible'
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Status Header */}
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                      {result.is_tampered ? (
                        <>
                          <GppBad sx={{ fontSize: 48, color: theme.palette.error.main }} />
                          <Box>
                            <Typography variant="h5" fontWeight={600} color={theme.palette.error.main}>
                              {tString('tamperingDetection.tamperingDetected')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {tString('tamperingDetection.tamperingDescription')}
                            </Typography>
                          </Box>
                        </>
                      ) : (
                        <>
                          <VerifiedUser sx={{ fontSize: 48, color: theme.palette.success.main }} />
                          <Box>
                            <Typography variant="h5" fontWeight={600} color={theme.palette.success.main}>
                              {tString('tamperingDetection.documentVerified')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {tString('tamperingDetection.verifiedDescription')}
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    {/* Metrics Grid */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                            {tString('tamperingDetection.severityLevel')}
                          </Typography>
                          <Chip
                            label={result.severity}
                            color={getSeverityColor(result.severity) as any}
                            size="medium"
                            sx={{ fontWeight: 600 }}
                          />
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                            {tString('tamperingDetection.riskScore')}
                          </Typography>
                          <Typography variant="h6" fontWeight={600} color={getRiskColor(result.risk_score)}>
                            {result.risk_score}/10
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* Risk Progress Bar */}
                    <Box sx={{ mb: 3 }}>
                      <LinearProgress
                        variant="determinate"
                        value={result.risk_score * 10}
                        sx={{
                          height: 12,
                          borderRadius: 2,
                          backgroundColor: (theme) => alpha(theme.palette.grey[300], 0.3),
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getRiskColor(result.risk_score),
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Box>

                    {/* Detected Issues */}
                    {result.flags && result.flags.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Warning fontSize="small" color="warning" />
                          {tString('tamperingDetection.detectedIssues')} ({result.flags.length})
                        </Typography>
                        <List dense sx={{ bgcolor: 'background.default', borderRadius: 1, mt: 1 }}>
                          {result.flags.map((flag, idx) => (
                            <ListItem key={idx} sx={{ py: 1 }}>
                              <ErrorIcon fontSize="small" color="warning" sx={{ mr: 1.5, flexShrink: 0 }} />
                              <ListItemText
                                primary={flag.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                secondary={flag.message}
                                primaryTypographyProps={{ fontWeight: 500, fontSize: '0.875rem' }}
                                secondaryTypographyProps={{ fontSize: '0.75rem' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}

                    <Divider sx={{ my: 2 }} />

                    {/* File Info */}
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Description fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                          {result.filename}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Timer fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                          {tString('tamperingDetection.analysisCompleted').replace('{0}', result.processing_time_ms.toString())}
                        </Typography>
                      </Stack>
                    </Stack>

                    {/* Action Button */}
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        setResult(null);
                        setError(null);
                      }}
                      sx={{
                        mt: 3,
                        borderRadius: 1,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderColor: brand.borderSubtle,
                        color: brand.textPrimary,
                        '&:hover': { borderColor: brand.orange, backgroundColor: 'transparent' },
                      }}
                    >
                      {tString('tamperingDetection.analyzeAnother')}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Container>
    </Box>
  );
};

export default TamperingDetection;
